import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import apiClient from '../../config/axios';
import previewText from '../../utils/suggestionPreview';

/**
 * The "Similar to this" rail (issue 189) — a short list of same-Family entries
 * sharing the target's declared categorical filter axes, computed server-side
 * (`GET /api/<segment>/:slug/similar`, server/routes/similar.js).
 *
 * It is PAGE CONTENT below the detail body, not a control in the action row
 * (#287 is deliberating how that row scales — this must not join it). It
 * fetches lazily AFTER the detail rendered, so the page's first paint is
 * untouched, and it renders NOTHING while loading, on error, or when the
 * server has no suggestions — a Family with no similarity axes (classes,
 * languages, any user Family declaring no enumerated filter) simply has no
 * rail, because a bad suggestion is worse than no suggestion.
 *
 * Each card spells its one-line preview through `previewText`, the SAME seam
 * the header search and type-ahead use — so a price renders in its authored
 * denomination and a CR under its authored prefix, and a Family the bundle has
 * never seen degrades to plain scalars rather than to nonsense. Links follow
 * the nav contract: `state.from` is the current detail URL so "Back" returns
 * here, and `listFrom` threads the originating list through reference hops.
 */
export default function SimilarRail({ segment, slug, from, listFrom, labelPlural }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    if (!slug) return undefined;
    apiClient
      .get(`/api/${segment}/${encodeURIComponent(slug)}/similar`)
      .then((res) => { if (!cancelled) setRows(res.data?.similar || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [segment, slug]);

  if (!rows || !rows.length) return null;

  return (
    <div className="card print:hidden" data-testid="similar-rail">
      <h2 className="section-header flex items-center gap-2">
        <Sparkles className="w-5 h-5" style={{ color: '#C9A84C' }} aria-hidden="true" />
        <span>Similar {labelPlural}</span>
      </h2>
      <ul className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((row) => {
          const preview = previewText(segment, row.preview);
          return (
            <li key={row.slug} className="min-w-0">
              <Link
                to={`/${segment}?slug=${encodeURIComponent(row.slug)}`}
                state={{ from, listFrom }}
                className="footer-link block truncate"
                data-preview={`${segment}/${row.slug}`}
              >
                {row.name}
              </Link>
              {preview && (
                <span className="block truncate text-xs" style={{ color: 'var(--text-muted)' }}>
                  {preview}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
